import { mockClients } from "../../../mocks/clients";
import { mockAppointments } from "../../../mocks/appointments";

const today = "2026-03-24";
const tomorrow = "2026-03-25";

const todayDeliveries = mockClients.filter((c) => c.deliveryDate === today && c.status !== "Tamamlandı");
const delayed = mockClients.filter((c) => {
  return c.deliveryDate < today && c.status !== "Tamamlandı";
});
const upcoming = mockClients.filter((c) => {
  return c.deliveryDate > today && c.deliveryDate <= "2026-03-31" && c.status !== "Tamamlandı";
});

const typeColors: Record<string, { bg: string; color: string; icon: string }> = {
  Sesli: { bg: "rgba(168,85,247,0.25)", color: "#d8b4fe", icon: "ri-mic-line" },
  Yazılı: { bg: "rgba(249,115,22,0.25)", color: "#fdba74", icon: "ri-file-text-line" },
  Günlük: { bg: "rgba(212,175,55,0.25)", color: "#fde68a", icon: "ri-sun-line" },
};

const statusStyle: Record<string, { bg: string; color: string }> = {
  "Yeni Başvuru": { bg: "rgba(212,175,55,0.22)", color: "#fde68a" },
  "Ödeme Bekliyor": { bg: "rgba(249,115,22,0.22)", color: "#fdba74" },
  Planlandı: { bg: "rgba(168,85,247,0.22)", color: "#d8b4fe" },
  Tamamlandı: { bg: "rgba(16,185,129,0.22)", color: "#6ee7b7" },
  Gecikti: { bg: "rgba(239,68,68,0.22)", color: "#fca5a5" },
};

function ClientRow({ client, urgent }: { client: (typeof mockClients)[0]; urgent?: boolean }) {
  const ts = typeColors[client.consultationType] || typeColors["Yazılı"];
  const ss = statusStyle[client.status] || { bg: "rgba(255,255,255,0.1)", color: "#fff" };

  return (
    <div
      className="flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all hover:bg-white/[0.06]"
      style={{
        background: urgent ? "rgba(239,68,68,0.1)" : "rgba(255,255,255,0.07)",
        border: urgent ? "1px solid rgba(239,68,68,0.35)" : "1px solid rgba(255,255,255,0.12)",
      }}
    >
      {urgent && (
        <div className="w-1.5 h-full min-h-[32px] rounded-full shrink-0 self-stretch" style={{ background: "#ef4444" }} />
      )}
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
        style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.5), rgba(212,175,55,0.4))", color: "#fff" }}>
        {client.avatar}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white whitespace-nowrap">{client.name}</p>
          {urgent && (
            <span className="text-xs px-2 py-0.5 rounded-full animate-pulse whitespace-nowrap font-medium"
              style={{ background: "rgba(239,68,68,0.3)", color: "#fca5a5" }}>
              Acil
            </span>
          )}
        </div>
        <p className="text-xs truncate font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>{client.service}</p>
      </div>
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
        style={{ background: ts.bg, color: ts.color }}>
        <i className={`${ts.icon} text-xs`} />
        {client.consultationType}
      </span>
      <span className="text-xs whitespace-nowrap font-mono font-medium" style={{ color: urgent ? "#fca5a5" : "rgba(255,255,255,0.7)" }}>
        {client.deliveryDate}
      </span>
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
        style={{ background: ss.bg, color: ss.color }}>
        {client.status}
      </span>
      <button className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:bg-white/10 shrink-0"
        style={{ color: "#34d399", border: "1px solid rgba(52,211,153,0.4)" }}>
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-check-line text-sm" />
        </div>
      </button>
    </div>
  );
}

export default function WorkTracking() {
  const totalPending = todayDeliveries.length + delayed.length + upcoming.length;
  const completedCount = mockClients.filter((c) => c.status === "Tamamlandı").length;
  const completionRate = Math.round((completedCount / mockClients.length) * 100);

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            ✦ İş Takip / Teslim Paneli
          </h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
            {totalPending} bekleyen iş · %{completionRate} tamamlanma oranı
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
            style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", color: "#10b981" }}>
            <i className="ri-checkbox-circle-line" />
            <span>{completedCount} tamamlandı</span>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="rounded-2xl px-5 py-4"
        style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white/85">Genel Tamamlanma</span>
          <span className="text-sm font-bold" style={{ color: "#fde68a" }}>%{completionRate}</span>
        </div>
        <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${completionRate}%`,
              background: "linear-gradient(90deg, #a855f7, #d4af37)",
            }}
          />
        </div>
        <div className="flex items-center gap-6 mt-3">
          {[
            { label: "Bugün teslim", value: todayDeliveries.length, color: "#fde68a" },
            { label: "Geciken", value: delayed.length, color: "#fca5a5" },
            { label: "Yaklaşan", value: upcoming.length, color: "#d8b4fe" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>{s.label}:</span>
              <span className="text-sm font-bold" style={{ color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-5 gap-5">
        {/* Main Lists */}
        <div className="col-span-3 space-y-5">
          {/* Today */}
          <div className="rounded-2xl overflow-hidden"
            style={{ border: "1px solid rgba(212,175,55,0.35)" }}>
            <div className="px-5 py-3.5 flex items-center justify-between"
              style={{ background: "rgba(212,175,55,0.15)", borderBottom: "1px solid rgba(212,175,55,0.25)" }}>
              <h2 className="font-semibold text-white flex items-center gap-2"
                style={{ fontFamily: "'Playfair Display', serif" }}>
                <i className="ri-sun-line text-sm" style={{ color: "#fde68a" }} />
                Bugün Teslim Edilecekler
              </h2>
              <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                style={{ background: "rgba(212,175,55,0.3)", color: "#fde68a" }}>
                {todayDeliveries.length}
              </span>
            </div>
            <div className="p-3 space-y-2" style={{ background: "rgba(0,0,0,0.2)" }}>
              {todayDeliveries.length === 0 ? (
                <div className="text-center py-6">
                  <i className="ri-checkbox-circle-line text-2xl" style={{ color: "rgba(52,211,153,0.6)" }} />
                  <p className="text-sm mt-2 font-medium" style={{ color: "rgba(255,255,255,0.55)" }}>Bugün teslim yok ✦</p>
                </div>
              ) : (
                todayDeliveries.map((c) => <ClientRow key={c.id} client={c} />)
              )}
            </div>
          </div>

          {/* Delayed */}
          <div className="rounded-2xl overflow-hidden"
            style={{ border: "1px solid rgba(239,68,68,0.4)" }}>
            <div className="px-5 py-3.5 flex items-center justify-between"
              style={{ background: "rgba(239,68,68,0.12)", borderBottom: "1px solid rgba(239,68,68,0.25)" }}>
              <h2 className="font-semibold flex items-center gap-2" style={{ color: "#fca5a5" }}>
                <i className="ri-alarm-warning-line text-sm" style={{ color: "#f87171" }} />
                Geciken Danışmanlıklar
              </h2>
              <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                style={{ background: "rgba(239,68,68,0.3)", color: "#fca5a5" }}>
                {delayed.length}
              </span>
            </div>
            <div className="p-3 space-y-2" style={{ background: "rgba(0,0,0,0.2)" }}>
              {delayed.length === 0 ? (
                <div className="text-center py-6">
                  <i className="ri-shield-check-line text-2xl" style={{ color: "rgba(52,211,153,0.6)" }} />
                  <p className="text-sm mt-2 font-medium" style={{ color: "rgba(255,255,255,0.55)" }}>Geciken iş yok ✦</p>
                </div>
              ) : (
                delayed.map((c) => <ClientRow key={c.id} client={c} urgent />)
              )}
            </div>
          </div>
        </div>

        {/* Upcoming */}
        <div className="col-span-2 rounded-2xl overflow-hidden"
          style={{ border: "1px solid rgba(168,85,247,0.35)" }}>
          <div className="px-5 py-3.5 flex items-center justify-between"
            style={{ background: "rgba(168,85,247,0.12)", borderBottom: "1px solid rgba(168,85,247,0.25)" }}>
            <h2 className="font-semibold text-white flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              <i className="ri-calendar-schedule-line text-sm" style={{ color: "#c084fc" }} />
              Yaklaşan İşler
            </h2>
            <span className="text-xs px-2.5 py-1 rounded-full font-medium"
              style={{ background: "rgba(168,85,247,0.3)", color: "#d8b4fe" }}>
              {upcoming.length}
            </span>
          </div>
          <div className="p-3 space-y-2" style={{ background: "rgba(0,0,0,0.2)" }}>
            {upcoming.map((c) => {
              const daysLeft = Math.ceil(
                (new Date(c.deliveryDate).getTime() - new Date(today).getTime()) / 86400000
              );
              return (
                <div key={c.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                    style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.5), rgba(212,175,55,0.4))", color: "#fff" }}>
                    {c.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{c.name}</p>
                    <p className="text-xs truncate font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>{c.service}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-mono font-medium" style={{ color: "rgba(255,255,255,0.7)" }}>{c.deliveryDate}</p>
                    <p className="text-xs mt-0.5 font-semibold" style={{ color: daysLeft <= 2 ? "#fbbf24" : "#c084fc" }}>
                      {daysLeft} gün
                    </p>
                  </div>
                </div>
              );
            })}
            {upcoming.length === 0 && (
              <div className="text-center py-6">
                <p className="text-sm font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>Yaklaşan iş yok ✦</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
